import { useCallback, useState } from 'react';

import { courseEntitlementApi } from './api-pb';
import { clearCourseClaimStatus, readCourseClaimStatus } from './course-claim';

/**
 * The human step between a handoff and a permanent account link.
 *
 * Nothing here runs by itself. Anyone enrolled on the course can get someone else's
 * browser to post their claim to the handoff — a form submission needs no cooperation
 * from the victim beyond visiting a page. If the claim were then redeemed on arrival,
 * that victim's Creator account would be tied to the attacker's course account for
 * good: every later attempt of their own answers 409, and the attacker can revoke the
 * plan at will. So the claim waits until someone reads whose account is about to be
 * linked and says yes.
 *
 * Declining costs nothing: the cookie carrying the claim expires three minutes after
 * the handoff set it.
 */
export function useCourseClaimPrompt(refresh: () => Promise<unknown>): {
  open: boolean;
  busy: boolean;
  result: string | null;
  confirm: () => Promise<void>;
  cancel: () => void;
} {
  const [open, setOpen] = useState(() => readCourseClaimStatus() === 'pending');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const confirm = useCallback(async () => {
    // A double click must not spend the claim twice: the second attempt would find a
    // burnt nonce and report "the link has expired" over a link that just worked.
    if (busy) return;
    setBusy(true);
    try {
      await courseEntitlementApi.claim();
      await refresh();
      setResult('active');
    } catch (error) {
      setResult((error as Error).message);
    } finally {
      clearCourseClaimStatus();
      setOpen(false);
      setBusy(false);
    }
  }, [busy, refresh]);

  const cancel = useCallback(() => {
    clearCourseClaimStatus();
    setOpen(false);
  }, []);

  return { open, busy, result, confirm, cancel };
}
